import Vue from "vue";
import {
	ERequestType,
	fetchGetHelper,
	fetchPostHelper,
} from "../../tools/fetch";

/*************************************
 State
 **/
const state = {
	cashDays: {},
	cashActions: {},
	unsubscribeCashDaysSnapShot: null,
	unsubscribeCashActionsSnapShot: null,
	isLoaded: false,
	cashObjEditing: {},
};

/*************************************
 Getters
 **/
const getters = {
	/** Service is loaded **/
	getCashDaysIsLoaded: function (state) {
		return state.isLoaded;
	},
	getCashDaysList: function (state) {
		return state.cashDays;
	},
	getCashStateForDayAndService: (state) => (id, service) => {
		if (
			Object.entries(state.cashDays).length > 0 &&
			Object.entries(state.cashDays[id]).length > 0 &&
			Object.entries(state.cashDays[id][service]).length > 0
		)
			return state.cashDays[id][service];
		return "";
	},
	getCashEditEntries: function (state) {
		if (state.cashObjEditing.days.length === 0) return {};
		// init min and max
		let minTs = state.cashObjEditing.days[0];
		let maxTs = state.cashObjEditing.days[state.cashObjEditing.days.length - 1];
		// get real min and max
		state.cashObjEditing.days.forEach((day) => {
			if (day < minTs) minTs = day;
			if (day > maxTs) maxTs = day;
		});
		// return values
		return {
			start: minTs,
			end: maxTs,
			service: state.cashObjEditing.service,
			type: state.cashDays[minTs][state.cashObjEditing.service].type,
			value: state.cashDays[minTs][state.cashObjEditing.service].value,
			nbCouv: state.cashDays[minTs][state.cashObjEditing.service].nbCouv,
			limitHour: state.cashDays[minTs][state.cashObjEditing.service].limitHour,
		};
	},
};

/*************************************
 Mutations
 **/
const mutations = {
	setDayAndServiceToCashDays(state, data) {
		// create timestamp entrie if doesn't exist
		if (!state.cashDays[data.tsId.toString()]) {
			Vue.set(state.cashDays, data.tsId.toString(), {});
		}

		// create service entrie if doesn't exist
		if (!state.cashDays[data.tsId.toString()][data.serviceId.toString()]) {
			Vue.set(
				state.cashDays[data.tsId.toString()],
				data.serviceId.toString(),
				data.content,
			);
		}
	},
	setLoadedState(state, val) {
		state.isLoaded = val;
	},
	resetStore(state) {
		state.cashDays = {};
		state.cashActions = {};
		state.unsubscribeCashDaysSnapShot = null;
		state.unsubscribeCashActionsSnapShot = null;
	},
	setCashEditEntry(state, data) {
		state.cashObjEditing = data;
	},
};

/*************************************
 Action
 **/
const actions = {
	/**
	 * Cleaners
	 **/
	cleanStore: function ({ state, rootState, commit, dispatch }) {
		dispatch("killSnapShot");
		commit("resetStore");
	},
	killSnapShot: function ({ state }) {
		if (state.unsubscribeCashDaysSnapShot !== null) {
			state.unsubscribeCashDaysSnapShot();
		}
	},

	/**
	 * Load Cash Day datas
	 **/
	loadCashDays: function ({
		state,
		rootState,
		commit,
		dispatch,
		getters,
		rootGetters,
	}) {
		return new Promise((resolve, reject) => {
			let userDatas = rootGetters["newUsers/getUser"];
			let uid = rootGetters["newUsers/getUidPublic"];
			if (!userDatas)
				return reject(new Error("loadClients - Error no user datas mDB"));
			fetchGetHelper(
				ERequestType.CASHDAYS.ALL,
				`restoid=${uid}`,
				userDatas.token,
			).then((res) => {
				// clean local datas
				state.cashDays = {};

				// get and populate datas
				res.data.forEach((cashday) => {
					commit("setDayAndServiceToCashDays", {
						tsId: cashday.ts.toString(),
						serviceId: cashday.service,
						content: {
							value: cashday.value,
							type: cashday.type,
							nbCouv: cashday.nbCouv,
							limitHour: cashday.limitHour,
						},
					});
				});
				// END loading
				commit("setLoadedState", true);
				resolve();
			});
		});
	},

	/**
	 * create Cash Day datas
	 * @param payload:Object {
	 *  - days: [String] array of timestamp
	 *  - service: String
	 *  - content: datas
	 * }
	 **/
	async createDaysAndService(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		// Promise
		return new Promise(async (resolve, reject) => {
			let userDatas = rootGetters["newUsers/getUser"];
			let uid = rootGetters["newUsers/getUidPublic"];
			// for tsIds create bdd entries
			for (const tsId of payload.days) {
				// create new cashday
				let newCashDay = {
					restoid: uid,
					ts: tsId.toString(),
					service: payload.service,
					value: payload.content.value,
					type: payload.content.type,
					nbCouv: payload.content.nbCouv,
					limitHour: payload.content.limitHour,
				};
				// send to db
				await fetchPostHelper(
					ERequestType.CASHDAYS.CREATE,
					newCashDay,
					userDatas.token,
				).catch((err) => {
					console.error(err);
					reject(err);
				});
			}
			// update local datas
			dispatch("loadCashDays");
			resolve();
		});
	},

	/**
	 * check Existing datas Cash Day
	 * @param payload:Object {
	 *  - days: [String] array of timestamp
	 *  - service: String
	 * }
	 **/
	async checkExistCashDays(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		return new Promise(async (resolve, reject) => {
			await fetchGetHelper(
				ERequestType.CASHDAYS.GET_BY_TS_SERVICE,
				`restoid=${payload.restoid}&ts=${payload.ts}&service=${payload.service}`,
			)
				.then((res) => {
					resolve(res.data);
				})
				.catch((err) => {
					console.error(err);
					reject(err);
				});
		});
	},

	/**
	 * check UnExisting datas Cash Day
	 * @param payload:Object {
	 *  - days: [String] array of timestamp
	 *  - service: String
	 * @return array
	 * }
	 **/
	getUnExistCashDays: function (
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		// bool
		let unExist = [];
		// for each days passed check if exist in state.cashDays
		payload.days.forEach((day) => {
			if (!state.cashDays[day] || !state.cashDays[day][payload.service])
				unExist.push(day);
		});
		// return bool result
		return unExist;
	},

	/**
	 * delete days datas Cash Day
	 * @param payload:Object {
	 *  - tIds: [String] array of timestamp
	 *  - service: String
	 * }
	 **/
	async deleteDayCashDays(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		// return new promise
		// delete in data base.then(resolve())
		let userDatas = rootGetters["newUsers/getUser"];
		let uid = rootGetters["newUsers/getUidPublic"];

		return new Promise(async (resolve, reject) => {
			// delete
			for (let i = 0; i < payload.tIds.length; i++) {
				// delete cashday object infos
				let deleteCashDay = {
					restoid: uid,
					ts: payload.tIds[i],
					service: payload.service,
				};
				// delete on db
				await fetchPostHelper(
					ERequestType.CASHDAYS.DELETE,
					deleteCashDay,
					userDatas.token,
				).catch((err) => {
					console.error(err);
					reject(err);
				});
			}
			// relaod datas
			dispatch("loadCashDays");
			resolve();
		});
	},
	/**
	 * delete days datas Cash Day
	 * @param payload:Object {
	 *  - tId: String timestamp
	 *  - service: String
	 * }
	 **/
	async deleteDayCashDay(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		// return new promise
		// delete in data base.then(resolve())
		let userDatas = rootGetters["newUsers/getUser"];
		let uid = rootGetters["newUsers/getUidPublic"];

		return new Promise(async (resolve, reject) => {
			// delete
			let deleteCashDay = {
				restoid: uid,
				ts: payload.tId,
				service: payload.service,
			};
			// delete on db
			await fetchPostHelper(
				ERequestType.CASHDAYS.DELETE,
				deleteCashDay,
				userDatas.token,
			).catch((err) => {
				console.error(err);
				reject(err);
			});

			dispatch("loadCashDays");
			resolve();
		});
	},
};

/*************************************
 Helpers
 **/

/*************************************
 Export module
 **/
export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
};
